<template>
  <COverflowWrapper :max-height="480">
    <NForm class="w-full" :show-feedback="false" size="medium">
      <NGrid :cols="1" :y-gap="32">
        <NFormItemGridItem :label="t('canvasUI.CThemeAssetsStep.logo')">
          <div class="flex flex-col gap-4 w-full">
            <div v-if="modelValue.logo" class="flex items-center gap-2 border rounded overflow-hidden shadow-sm p-2">
              <div class="w-[60px] h-[60px] bg-gray-50 flex items-center justify-center flex-shrink-0">
                <img alt="Logo" class="max-h-[50px] max-w-[50px] object-contain" :src="modelValue.logo.url" />
              </div>
              <div class="flex-1 min-w-0 flex items-center">
                <span class="text-sm color-text truncate max-w-[calc(100%-70px)]">{{ modelValue.logo.name }}</span>
                <div class="flex gap-1 ml-auto flex-shrink-0">
                  <NButton class="p-2 rounded" text @click="openLogoSelector">
                    <CIcon icon="edit" />
                  </NButton>
                  <NButton class="p-2 rounded" text @click="removeLogo">
                    <CIcon class="color-error" icon="trash" />
                  </NButton>
                </div>
              </div>
            </div>
            <div v-if="!modelValue.logo" class="flex justify-center">
              <CButton @click="openLogoSelector">
                {{ t('canvasUI.CThemeAssetsStep.selectLogo') }}
              </CButton>
            </div>
          </div>
        </NFormItemGridItem>

        <NFormItemGridItem :label="t('canvasUI.CThemeAssetsStep.images', { count: modelValue.images.length })">
          <div class="flex flex-col gap-4 w-full">
            <div v-if="modelValue.images.length" class="flex flex-col gap-2 border rounded px-2 shadow-sm">
              <Draggable v-model="images" handle=".image-item-handle" item-key="id">
                <div
                  v-for="(image, index) in images"
                  :key="image.id"
                  class="flex items-center gap-2 py-2"
                  :class="{ 'border-b': index !== modelValue.images.length - 1 }"
                >
                  <CIcon
                    class="image-item-handle px-1 cursor-move flex-shrink-0 color-primary3"
                    fa-type="fas"
                    icon="grip-dots-vertical"
                    size="16px"
                  />
                  <div class="w-[60px] h-[60px] bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <img alt="Image" class="max-h-[50px] max-w-[50px] object-contain" :src="image.url" />
                  </div>
                  <div class="flex-1 min-w-0 flex items-center">
                    <span class="text-sm color-text truncate max-w-[calc(100%-40px)]">{{ image.name }}</span>
                    <NButton class="p-2 ml-auto flex-shrink-0" text @click="removeImage(index)">
                      <CIcon class="color-error" icon="trash" />
                    </NButton>
                  </div>
                </div>
              </Draggable>
            </div>

            <div class="flex justify-center mt-2">
              <CButton @click="openImageSelectorWithPreselected">
                {{
                  modelValue.images.length
                    ? t('canvasUI.CThemeAssetsStep.manageImages')
                    : t('canvasUI.CThemeAssetsStep.selectImages')
                }}
              </CButton>
            </div>
          </div>
        </NFormItemGridItem>
      </NGrid>
    </NForm>
  </COverflowWrapper>
</template>

<script setup lang="ts">
import { inject, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { NForm, NFormItemGridItem, NGrid, NButton } from 'naive-ui'
import { FileContentTypeEnum } from '@openapi'
import { VueDraggable as Draggable } from 'vue-draggable-plus'

import COverflowWrapper from '@lib/components/COverflowWrapper/COverflowWrapper.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'

import type { CanvasThemeAsset } from '@lib/types/canvasTheme'
import type { AdminSelectContentResponse } from '@lib/sdk/api/modules/admin/types.admin'

const props = defineProps<{
  modelValue: {
    logo?: CanvasThemeAsset
    images: CanvasThemeAsset[]
  }
}>()

const emit = defineEmits<{
  'update:modelValue': [value: { logo?: CanvasThemeAsset | null; images: CanvasThemeAsset[] }]
}>()

const selectContent = inject<(payload: any) => Promise<AdminSelectContentResponse>>('selectContent')

const { t } = useI18n()

const images = computed({
  get: () => {
    return props.modelValue.images
  },
  set: (newImages) => {
    emit('update:modelValue', {
      ...props.modelValue,
      images: newImages,
    })
  },
})

function removeLogo() {
  emit('update:modelValue', { ...props.modelValue, logo: null })
}

function removeImage(index: number) {
  const updatedImages = [...images.value]
  updatedImages.splice(index, 1)
  emit('update:modelValue', {
    ...props.modelValue,
    images: updatedImages,
  })
}

async function openLogoSelector() {
  if (!selectContent) {
    console.error('selectContent function is not provided')
    return
  }

  try {
    // Create selections array if we have an existing logo
    const selections = props.modelValue.logo
      ? [
          {
            fileId: props.modelValue.logo.id || props.modelValue.logo.url,
            type: 'asset',
          },
        ]
      : undefined

    const result = await selectContent({
      allowed_asset_content_types: [FileContentTypeEnum.IMAGE],
      allowed_selector_types: ['assets'],
      max_selections: 1,
      selections: selections,
    })

    if (result.user_action === 'selected') {
      const selectedItems = result.content || []

      if (selectedItems.length) {
        const selected = selectedItems[0]

        const logoAsset: CanvasThemeAsset = {
          name: selected.name ?? '',
          url: selected.thumbnail_url ?? '',
          id: selected.id,
        }

        emit('update:modelValue', {
          ...props.modelValue,
          logo: logoAsset,
        })
      } else {
        // If no items selected but user action was 'selected', treat as clearing the logo
        emit('update:modelValue', {
          ...props.modelValue,
          logo: undefined,
        })
      }
    }
  } catch (error) {
    console.error('Error selecting logo:', error)
  }
}

async function openImageSelectorWithPreselected() {
  if (!selectContent) {
    console.error('selectContent function is not provided')
    return
  }

  try {
    // Map current images to the format expected by the content selector
    const selections = props.modelValue.images.map((image) => ({
      fileId: image.id || image.url,
      type: 'asset',
    }))

    const result = await selectContent({
      allowed_asset_content_types: [FileContentTypeEnum.IMAGE],
      allowed_selector_types: ['assets'],
      selections,
    })

    if (result.user_action === 'selected') {
      const selectedItems = result.content || []

      if (selectedItems.length) {
        const newImages = selectedItems.map((selected: any) => {
          return {
            name: selected.name || selected.file?.name || '',
            url: selected.thumbnail_url || '',
            id: selected.id,
          } as CanvasThemeAsset
        })

        // Replace all images with the new selection
        emit('update:modelValue', {
          ...props.modelValue,
          images: newImages,
        })
      } else {
        // If no items were selected but user confirmed, clear all images
        emit('update:modelValue', {
          ...props.modelValue,
          images: [],
        })
      }
    }
  } catch (error) {
    console.error('Error selecting images:', error)
  }
}
</script>
